/**
 * AI-powered secondhand search page, routed at "/search" (app/App.tsx).
 * Three-step flow: floating category picker -> SearchForm (query, optional
 * image, match type, price range) -> streamed results grid. Streams results
 * via SearchAPI.searchStream, handling per-domain result batches, scraper
 * health, verification filtering, AI ranking reorder, and color-palette
 * events (with client-side color filtering). For gendered categories it
 * detects gender from the query or prompts via GenderDialog (SearchQuery.tsx).
 * Requires a logged-in user (UserContext); otherwise prompts to sign up.
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Categories, Category, Sex } from "@/types/category";
import { SearchAPI, SearchSingleResult, SearchStreamEvent, ColorPaletteEntry } from "@/api/search";
import { MatchType } from "@/types/bounty";
import { useUserState } from "@/contexts/UserContext";
import AlertDialog from "@/components/AlertDialog";
import SearchForm from "./SearchForm";
import { detectGenderFromQuery, categoryHasGender, GenderDialog } from "./SearchQuery";

type SelectionStep = "category" | "search" | "results";

interface FloatingPosition {
    x: number;
    y: number;
    rotation: number;
    scale: number;
    delay: number;
}

// Pre-defined jumbled positions for categories (percentages from center)
// Order: fashion, watches, jewelry, furniture, art
// Desktop positions
const CATEGORY_POSITIONS_DESKTOP: FloatingPosition[] = [
    { x: -18, y: -12, rotation: -6, scale: 1, delay: 0 }, // fashion - upper left
    { x: 16, y: -14, rotation: 4, scale: 1, delay: 100 }, // watches - upper right
    { x: -15, y: 12, rotation: 5, scale: 1, delay: 200 }, // jewelry - lower left
    { x: 18, y: 10, rotation: -3, scale: 1, delay: 300 }, // furniture - lower right
    { x: 0, y: 0, rotation: 2, scale: 1, delay: 150 }, // art - center
];

// Mobile positions - organic scattered layout with vertical spread
// Order matches Categories: fashion, watches, jewelry, furniture, art
const CATEGORY_POSITIONS_MOBILE: FloatingPosition[] = [
    { x: -12, y: -28, rotation: -5, scale: 1, delay: 0 }, // fashion - upper left
    { x: 15, y: -12, rotation: 6, scale: 1, delay: 100 }, // watches - right
    { x: -18, y: 4, rotation: -4, scale: 1, delay: 200 }, // jewelry - left
    { x: 12, y: 20, rotation: 5, scale: 1, delay: 300 }, // furniture - lower right
    { x: -12, y: 36, rotation: -3, scale: 1, delay: 150 }, // art - bottom
];


const FloatingText: React.FC<{
    label: string;
    position: FloatingPosition;
    onClick: () => void;
    animationIndex: number;
}> = ({ label, position, onClick, animationIndex }) => (
    <button
        onClick={onClick}
        className="absolute font-porsui text-black hover:opacity-60 
            transition-opacity duration-300 ease-out cursor-pointer animate-float 
            text-xl sm:text-2xl tracking-widest whitespace-nowrap"
        style={
            {
                left: `calc(50% + ${position.x}%)`,
                top: `calc(50% + ${position.y}%)`,
                transform: `translate(-50%, -50%) rotate(${position.rotation}deg)`,
                "--float-delay": `${position.delay + animationIndex * 80}ms`,
            } as React.CSSProperties
        }
    >
        {label}
    </button>
);

const BackButton: React.FC<{ onClick: () => void; label: string }> = ({ onClick, label }) => (
    <button
        onClick={onClick}
        className="flex items-center gap-2 text-slate-400 hover:text-slate-700 transition-colors duration-200 font-porsui text-sm mb-4"
    >
        <span className="text-lg">←</span>
        {label}
    </button>
);

interface SearchResult extends SearchSingleResult {
    domain: string;
}

const SearchPage: React.FC = () => {
    const navigate = useNavigate();
    const user = useUserState();

    const [category, setCategory] = useState<Category | null>(null);
    const [step, setStep] = useState<SelectionStep>("category");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchType, setSearchType] = useState<MatchType>("exact");
    const [minPrice, setMinPrice] = useState<string>("");
    const [maxPrice, setMaxPrice] = useState<string>("");
    const [_imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isRanking, setIsRanking] = useState(false);
    const [rankingComplete, setRankingComplete] = useState(false);
    const [completedDomains, setCompletedDomains] = useState<string[]>([]);
    const [showAuthDialog, setShowAuthDialog] = useState(false);
    const [showRankingDialog, setShowRankingDialog] = useState(false);
    const [colorPalette, setColorPalette] = useState<ColorPaletteEntry[]>([]);
    const [colorMap, setColorMap] = useState<Record<string, string>>({});
    const [selectedColors, setSelectedColors] = useState<Set<string>>(new Set());
    const [scraperOperational, setScraperOperational] = useState<boolean | null>(null);
    const [showGenderDialog, setShowGenderDialog] = useState(false);
    const [selectedSex, setSelectedSex] = useState<Sex | null>(null);
    const abortRef = useRef<(() => void) | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 640);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const categoryPositions = isMobile ? CATEGORY_POSITIONS_MOBILE : CATEGORY_POSITIONS_DESKTOP;

    const handleCategorySelect = useCallback(
        (cat: Category) => {
            if (!user) {
                setShowAuthDialog(true);
                return;
            }
            setCategory(cat);
            setStep("search");
        },
        [user]
    );

    const handleAuthDialogConfirm = useCallback(() => {
        setShowAuthDialog(false);
        navigate("/signUpLogin");
    }, [navigate]);

    const handleAuthDialogCancel = useCallback(() => {
        setShowAuthDialog(false);
    }, []);

    const handleBack = useCallback(() => {
        if (abortRef.current) {
            abortRef.current();
            abortRef.current = null;
        }

        if (step === "search") {
            setCategory(null);
            setStep("category");
        } else if (step === "results") {
            setResults([]);
            setCompletedDomains([]);
            setIsSearching(false);
            setIsRanking(false);
            setRankingComplete(false);
            setShowRankingDialog(false);
            setColorPalette([]);
            setColorMap({});
            setSelectedColors(new Set());
            setScraperOperational(null);
            setSelectedSex(null);
            setStep("search");
        }
    }, [step]);

    const handleStartOver = useCallback(() => {
        if (abortRef.current) {
            abortRef.current();
            abortRef.current = null;
        }
        setCategory(null);
        setSearchQuery("");
        setSearchType("exact");
        setMinPrice("");
        setMaxPrice("");
        setImageFile(null);
        setImagePreview(null);
        setResults([]);
        setCompletedDomains([]);
        setIsSearching(false);
        setIsRanking(false);
        setRankingComplete(false);
        setShowRankingDialog(false);
        setColorPalette([]);
        setColorMap({});
        setSelectedColors(new Set());
        setScraperOperational(null);
        setSelectedSex(null);
        setStep("category");
    }, []);

    const handleImageSelect = useCallback((file: File) => {
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    }, []);

    const handleImageRemove = useCallback(() => {
        setImageFile(null);
        setImagePreview(null);
        setSearchType("exact");
    }, []);

    const canSearch = searchQuery.trim().length >= 10;

    const executeSearch = useCallback(
        (sex: Sex | null) => {
            if (!category || !canSearch) return;

            setResults([]);
            setCompletedDomains([]);
            setIsSearching(true);
            setIsRanking(false);
            setRankingComplete(false);
            setShowRankingDialog(false);
            setColorPalette([]);
            setColorMap({});
            setSelectedColors(new Set());
            setScraperOperational(null);
            setSelectedSex(sex);
            setStep("results");

            const abort = SearchAPI.searchStream(
                {
                    category,
                    sex,
                    category_type: null,
                    query: searchQuery.trim(),
                    search_type: searchType,
                    min_price: minPrice ? parseInt(minPrice, 10) : undefined,
                    max_price: maxPrice ? parseInt(maxPrice, 10) : undefined,
                },
            (event: SearchStreamEvent) => {
                if (event.scraper_operational !== undefined) {
                    setScraperOperational(event.scraper_operational);
                    return;
                }
                if (event.domain && event.results) {
                    const newResults: SearchResult[] = event.results.results.map((r) => ({
                        ...r,
                        domain: event.domain!,
                    }));
                    setResults((prev) => [...prev, ...newResults]);
                    setCompletedDomains((prev) => [...prev, event.domain!]);
                } else if (event.ranking_started) {
                    setIsSearching(false);
                    setIsRanking(true);
                } else if (event.verified) {
                    const verifiedSet = new Set(event.verified);
                    setResults((prev) =>
                        prev.filter((r) => verifiedSet.has(r.product_url))
                    );
                } else if (event.ranking) {
                    setResults((prev) => {
                        const byUrl = new Map<string, SearchResult>();
                        for (const r of prev) {
                            byUrl.set(r.product_url, r);
                        }
                        const reordered: SearchResult[] = [];
                        for (const url of event.ranking!) {
                            const r = byUrl.get(url);
                            if (r) reordered.push(r);
                        }
                        for (const r of prev) {
                            if (!reordered.includes(r)) reordered.push(r);
                        }
                        return reordered;
                    });
                    setIsRanking(false);
                    setRankingComplete(true);
                    setShowRankingDialog(true);
                    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                } else if (event.colors) {
                    setColorPalette(event.colors.palette);
                    setColorMap(event.colors.results);
                } else if (event.ranking_error) {
                    console.error("Ranking error:", event.ranking_error);
                    setIsRanking(false);
                }
            },
            (error) => {
                console.error("Search error:", error);
                setIsSearching(false);
                setIsRanking(false);
            },
            () => {
                setIsSearching(false);
                setIsRanking(false);
            }
        );

            abortRef.current = abort;
        },
        [category, searchQuery, searchType, minPrice, maxPrice, canSearch]
    );

    const handleGenderChoice = useCallback(
        (choice: "male" | "female" | "either") => {
            setShowGenderDialog(false);
            executeSearch(choice === "either" ? null : choice);
        },
        [executeSearch]
    );

    const handleSearch = useCallback(() => {
        if (!category || !canSearch) return;

        if (categoryHasGender(category)) {
            const detected = detectGenderFromQuery(searchQuery);
            if (detected) {
                executeSearch(detected);
            } else {
                setShowGenderDialog(true);
            }
        } else {
            executeSearch(null);
        }
    }, [category, canSearch, searchQuery, executeSearch]);

    const breadcrumbs: string[] = [];
    if (category) breadcrumbs.push(category);
    if (step === "results" && searchQuery) {
        const sexLabel = selectedSex === "male" ? " (men)" : selectedSex === "female" ? " (women)" : "";
        breadcrumbs.push(`"${searchQuery}${sexLabel}"`);
    }

    const isSearchOrResults = step === "search" || step === "results";

    const handleDismissRankingDialog = useCallback(() => {
        setShowRankingDialog(false);
    }, []);

    const handleToggleColor = useCallback((colorName: string) => {
        setSelectedColors((prev) => {
            const next = new Set(prev);
            if (next.has(colorName)) {
                next.delete(colorName);
            } else {
                next.add(colorName);
            }
            return next;
        });
    }, []);

    const handleClearColors = useCallback(() => {
        setSelectedColors(new Set());
    }, []);

    // Build per-color counts from the current results (respects verified/ranked state)
    const colorCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const r of results) {
            const color = colorMap[r.product_url];
            if (color) {
                counts[color] = (counts[color] || 0) + 1;
            }
        }
        return counts;
    }, [results, colorMap]);

    // If the original query already mentions a color, suppress the filter entirely
    const queryContainsColor = useMemo(() => {
        if (!searchQuery || colorPalette.length === 0) return false;
        const words = searchQuery.toLowerCase().split(/\s+/);
        return colorPalette.some((c) => words.includes(c.name));
    }, [searchQuery, colorPalette]);

    // Only show palette entries that actually appear in the results
    const availableColors = useMemo(
        () =>
            queryContainsColor ? [] : colorPalette.filter((c) => (colorCounts[c.name] || 0) > 0),
        [colorPalette, colorCounts, queryContainsColor]
    );

    // Apply colour filter to results
    const filteredResults = useMemo(() => {
        if (selectedColors.size === 0) return results;
        return results.filter((r) => {
            const color = colorMap[r.product_url];
            return color && selectedColors.has(color);
        });
    }, [results, colorMap, selectedColors]);

    return (
        <>
            {showAuthDialog && (
                <AlertDialog
                    title="create an account"
                    message="sign up or log in to use our ai-powered search and find exactly what you're looking for."
                    confirmText="sign up"
                    cancelText="cancel"
                    onConfirm={handleAuthDialogConfirm}
                    onCancel={handleAuthDialogCancel}
                />
            )}
            {showRankingDialog && (
                <AlertDialog
                    title="ai ranking complete"
                    message={`your ${results.length} results have been verified and ranked by our ai for the best relevance.`}
                    confirmText="got it"
                    cancelText="dismiss"
                    onConfirm={handleDismissRankingDialog}
                    onCancel={handleDismissRankingDialog}
                />
            )}
            {showGenderDialog && (
                <GenderDialog
                    onChoice={handleGenderChoice}
                    onDismiss={() => setShowGenderDialog(false)}
                />
            )}
            <div
                ref={scrollContainerRef}
                className={`flex-1 flex flex-col px-4 min-h-[calc(100vh-4rem)] relative
            ${
                isSearchOrResults
                    ? "overflow-y-auto"
                    : "items-center justify-center overflow-hidden"
            }`}
            >
                {/* Page title */}
                {step === "category" && (
                    <div className="absolute top-4 sm:top-8 left-0 right-0 flex justify-center animate-fade-in z-10 px-4">
                        <h1 className="font-porsui text-black text-base sm:text-xl tracking-widest text-center leading-relaxed">
                            start your ai-powered secondhand search here
                        </h1>
                    </div>
                )}

                {/* Header with breadcrumbs - fixed for search/results */}
                {step !== "category" && (
                    <div
                        className={`${
                            isSearchOrResults
                                ? "sticky top-0 pt-6 pb-4 bg-slate-50 z-20"
                                : "absolute top-8"
                        } 
                    left-0 right-0 flex flex-col items-center gap-2 animate-fade-in`}
                    >
                        <BackButton
                            onClick={handleBack}
                            label="back"
                        />
                        <div className="flex items-center gap-3 text-slate-300 font-porsui">
                            {breadcrumbs.map((crumb, index) => (
                                <React.Fragment key={crumb}>
                                    {index > 0 && <span className="text-slate-300">/</span>}
                                    <span className="text-slate-600">{crumb}</span>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                )}

                {/* Floating category selection */}
                {!isSearchOrResults && (
                    <div className="relative w-full h-[70vh] sm:h-[60vh] max-w-4xl mt-4">
                        {step === "category" &&
                            Categories.map((cat, index) => (
                                <FloatingText
                                    key={cat}
                                    label={cat}
                                    position={categoryPositions[index]}
                                    onClick={() => handleCategorySelect(cat)}
                                    animationIndex={index}
                                />
                            ))}
                    </div>
                )}

                {/* Search Form - Full page layout */}
                {step === "search" && (
                    <SearchForm
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        searchType={searchType}
                        setSearchType={setSearchType}
                        minPrice={minPrice}
                        setMinPrice={setMinPrice}
                        maxPrice={maxPrice}
                        setMaxPrice={setMaxPrice}
                        imagePreview={imagePreview}
                        onImageSelect={handleImageSelect}
                        onImageRemove={handleImageRemove}
                        canSearch={canSearch}
                        onSearch={handleSearch}
                        onStartOver={handleStartOver}
                    />
                )}

                {/* Results - Full page layout */}
                {step === "results" && (
                    <div className="w-full max-w-7xl mx-auto pb-12">
                        {/* Scraper connection status */}
                        {scraperOperational !== null && (
                            <div className="flex items-center justify-center gap-2 pt-6 pb-2 animate-fade-in">
                                <span
                                    className={`w-2 h-2 rounded-full shrink-0 ${
                                        scraperOperational
                                            ? "bg-emerald-400"
                                            : "bg-red-400 animate-pulse"
                                    }`}
                                />
                                <span className="font-porsui text-[11px] tracking-wider text-slate-400">
                                    {scraperOperational
                                        ? "data source reachable"
                                        : "data source unreachable"}
                                </span>
                            </div>
                        )}

                        {/* Loading indicator — searching phase */}
                        {isSearching && (
                            <div className="flex items-center justify-center gap-3 py-8 animate-fade-in">
                                <div className="flex gap-1">
                                    <div
                                        className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                                        style={{ animationDelay: "0ms" }}
                                    />
                                    <div
                                        className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                                        style={{ animationDelay: "150ms" }}
                                    />
                                    <div
                                        className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"
                                        style={{ animationDelay: "300ms" }}
                                    />
                                </div>
                                <span className="font-porsui text-slate-500 text-sm">
                                    searching{" "}
                                    {completedDomains.length > 0 &&
                                        `· ${completedDomains.length} sources`}
                                </span>
                            </div>
                        )}

                        {/* Loading indicator — ranking phase */}
                        {isRanking && (
                            <div className="flex items-center justify-center gap-3 py-8 animate-fade-in">
                                <div className="flex gap-1">
                                    <div
                                        className="w-2 h-2 bg-slate-600 rounded-full animate-bounce"
                                        style={{ animationDelay: "0ms" }}
                                    />
                                    <div
                                        className="w-2 h-2 bg-slate-600 rounded-full animate-bounce"
                                        style={{ animationDelay: "150ms" }}
                                    />
                                    <div
                                        className="w-2 h-2 bg-slate-600 rounded-full animate-bounce"
                                        style={{ animationDelay: "300ms" }}
                                    />
                                </div>
                                <span className="font-porsui text-slate-500 text-sm">
                                    ranking {results.length} results with ai
                                </span>
                            </div>
                        )}

                        {/* Completed message */}
                        {!isSearching && !isRanking && results.length > 0 && (
                            <div className="text-center py-6 animate-fade-in">
                                <span className="font-porsui text-slate-500 text-sm">
                                    {filteredResults.length}
                                    {selectedColors.size > 0 ? ` of ${results.length}` : ""} results
                                    from {completedDomains.length} sources
                                    {rankingComplete && " · ai ranked"}
                                </span>
                            </div>
                        )}

                        {/* Mobile color filter — horizontal scrollable strip (above flex row) */}
                        {availableColors.length > 0 && (
                            <div className="lg:hidden w-full mb-4 animate-fade-in">
                                <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                                    <span className="font-porsui text-[10px] tracking-[0.15em] uppercase text-slate-400 shrink-0 mr-1">
                                        color
                                    </span>
                                    {selectedColors.size > 0 && (
                                        <button
                                            onClick={handleClearColors}
                                            className="font-porsui text-[10px] text-slate-400 hover:text-slate-600 
                                                tracking-wider uppercase shrink-0 mr-1"
                                        >
                                            clear
                                        </button>
                                    )}
                                    {availableColors.map((color) => {
                                        const isSelected = selectedColors.has(color.name);
                                        return (
                                            <button
                                                key={color.name}
                                                onClick={() => handleToggleColor(color.name)}
                                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border shrink-0 
                                                    transition-all duration-200
                                                    ${
                                                        isSelected
                                                            ? "border-slate-400 bg-slate-100"
                                                            : "border-slate-200 hover:border-slate-300"
                                                    }`}
                                            >
                                                <span
                                                    className={`w-3 h-3 rounded-full shrink-0
                                                        ${color.name === "white" ? "border border-slate-300" : ""}`}
                                                    style={{ backgroundColor: color.hex }}
                                                />
                                                <span className="font-porsui text-[10px] text-slate-600 tracking-wider">
                                                    {color.name}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Results with optional color sidebar */}
                        <div className="flex gap-6">
                            {/* Color filter sidebar — desktop only */}
                            {availableColors.length > 0 && (
                                <aside className="hidden lg:block w-44 shrink-0 animate-fade-in">
                                    <div className="sticky top-28">
                                        <div className="flex items-center justify-between mb-4">
                                            <span className="font-porsui text-[11px] tracking-[0.2em] uppercase text-black">
                                                color
                                            </span>
                                            {selectedColors.size > 0 && (
                                                <button
                                                    onClick={handleClearColors}
                                                    className="font-porsui text-[10px] text-slate-400 hover:text-slate-600 
                                                        tracking-wider uppercase transition-colors duration-200"
                                                >
                                                    clear
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            {availableColors.map((color) => {
                                                const isSelected = selectedColors.has(color.name);
                                                const count = colorCounts[color.name] || 0;
                                                return (
                                                    <button
                                                        key={color.name}
                                                        onClick={() =>
                                                            handleToggleColor(color.name)
                                                        }
                                                        className={`flex items-center gap-3 px-2 py-1.5 rounded-lg transition-all duration-200
                                                            ${
                                                                isSelected
                                                                    ? "bg-slate-100 ring-1 ring-slate-300"
                                                                    : "hover:bg-slate-50"
                                                            }`}
                                                    >
                                                        <span
                                                            className={`w-4 h-4 rounded-full border shrink-0 transition-shadow duration-200
                                                                ${isSelected ? "ring-2 ring-offset-1 ring-slate-400" : ""}
                                                                ${color.name === "white" ? "border-slate-300" : "border-transparent"}`}
                                                            style={{ backgroundColor: color.hex }}
                                                        />
                                                        <span className="font-porsui text-xs text-slate-600 tracking-wider">
                                                            {color.name}
                                                        </span>
                                                        <span className="font-porsui text-[10px] text-slate-400 ml-auto">
                                                            {count}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </aside>
                            )}

                            {/* Results grid */}
                            <div className="flex-1 min-w-0">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4">
                                    {filteredResults.map((result, index) => (
                                        <a
                                            key={`${result.product_url}-${index}`}
                                            href={result.product_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 
                                            hover:shadow-xl hover:border-slate-200 hover:-translate-y-1
                                            transition-all duration-300 animate-fade-in"
                                        >
                                            <div className="aspect-square overflow-hidden bg-slate-50">
                                                <img
                                                    src={result.image_url}
                                                    alt={result.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                    loading="lazy"
                                                />
                                            </div>
                                            <div className="p-4">
                                                <p className="font-porsui text-[10px] text-slate-400 uppercase tracking-wider mb-1">
                                                    {result.domain}
                                                </p>
                                                <h3 className="font-porsui text-sm text-slate-800 line-clamp-2 mb-2 leading-snug">
                                                    {result.title}
                                                </h3>
                                                <p className="font-porsui text-base font-medium text-slate-900">
                                                    ${result.price}
                                                </p>
                                                {result.size && (
                                                    <p className="font-porsui text-xs text-slate-400 mt-1">
                                                        {result.size}
                                                    </p>
                                                )}
                                            </div>
                                        </a>
                                    ))}
                                </div>

                                {/* No results */}
                                {!isSearching && filteredResults.length === 0 && (
                                    <div className="flex flex-col items-center justify-center py-20 animate-fade-in">
                                        <p className="font-porsui text-slate-400 text-lg mb-4">
                                            {selectedColors.size > 0
                                                ? "no results match the selected colors"
                                                : "no results found"}
                                        </p>
                                        <button
                                            onClick={
                                                selectedColors.size > 0
                                                    ? handleClearColors
                                                    : handleBack
                                            }
                                            className="font-porsui text-slate-500 hover:text-slate-700 text-sm transition-colors
                                            border border-slate-200 px-4 py-2 rounded-lg hover:border-slate-300"
                                        >
                                            {selectedColors.size > 0
                                                ? "clear color filter"
                                                : "try a different search"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default SearchPage;
